import type { Breakpoint } from "antd/es/_util/responsiveObserver";
import { AccountInfo, ProfileAccountInfo } from "./accountInterface.interface";
import {
  BasePagination,
  CommonPageResponse,
  CommonResponse,
  LEVEL_TYPE,
} from "./commonInterface.interface";
import { UserDetail, UserInfo } from "./userInterface.interface";
import { RecorderType } from "utils/RecorderUtil";
import { ColumnType } from "antd/es/table";
import { StatusType } from "components/blocks/status/Status";
import {
  EventHistoryDto,
  StatCardResultDto,
  YearPaymentPrice,
} from "./planInterface.interface";
import { CloudModalAccount } from "pages/subscription/modal/addCloud/AddCloudModal";

export const enum SUBSCRIPTION_LOCATION {
  ORGANIZATION = "/subscription/organization",
  KEYS = "/subscription/keys",
  KEYS_ORGANIZATION = "/subscription/keys/organization",
  KEYS_REQUEST_HISTORY = "/subscription/keys/request-history",
  KEYS_LIST = "/subscription/keys/list",
  SERVICES = "/subscription/services",
  LYVE = "/subscription/lyve",
}

export enum CUMULUS_TYPE {
  LYVE = "LYVE",
  WASABI = "WASABI",
}

export interface HistoryStorageActive {
  key: string;
  capacity: number;
  duration: number;
  created: string;
  activated: string;
  expirationDate: string;
  status: string;
}

export enum SUBSCRIPTION_TYPE {
  STANDARD = "STANDARD",
  STORAGE = "STORAGE",
}

/**
 * expire date 잔여일에 따른 상태값
 */
export enum SUBSCRIPTION_STATUS {
  NONE = "NONE",
  ACTIVATED = "ACTIVATED",
  EXPIRED_WARNING = "EXPIRED_WARNING", // 31 ~ 60
  EXPIRED_SOON = "EXPIRED_SOON", // 15 ~30
  EXPIRED = "EXPIRED", // 1 ~ 14
  EXPIRED_END = "EXPIRED_END",
}

export enum DISCOUNT_TIER {
  PLATINUM = "PLATINUM",
  GOLD = "GOLD",
  SILVER = "SILVER",
  CUSTOM = "CUSTOM",
  AUTHORIZED = "AUTHORIZED",
}

export enum CHECKOUT_STATUS {
  SUCCESS = "CHECKOUT_SUCCESS",
  FAIL = "CHECKOUT_FAIL",
}

export interface SelectedRowAccountInfo {
  accountId: string;
  accountLevel: LEVEL_TYPE;
  accountNumber: string;
  parentLevel: LEVEL_TYPE;
  isUsagePaid: boolean;
}

export interface InstallerList {
  name: string;
  email: string;
  phone: string;
  id: string;
}

export interface GetSubscriptionInstallerResponse extends CommonResponse {
  result: InstallerList[];
}
export interface GetLyveListResponse extends CommonPageResponse {
  result: LicenseDetailData[];
}
export interface CreateGenerateKeyResponse extends CommonResponse {
  result: {
    subscriptionKeys: string[];
  };
}

export interface UpdateLyveStorageSizeRequest {
  id: string;
  size: number;
}
export interface UpdateLyveStorageSizeResponse extends CommonPageResponse {
  result: string;
}

export enum STORAGE_LOCATION {
  US_EAST_1 = "us-east-1",
  US_EAST_2 = "us-east-2",
  US_CENTRAL_2 = "us-central-2",
  US_WEST_2 = "us-west-2",
  EU_WEST_1 = "eu-west-1",
}
export interface LicenseData {
  cpp: string;
  partNumber: string;
  location: STORAGE_LOCATION;
  qty: 1;
  comment: string;
  provider: CUMULUS_TYPE;
}
export interface LicenseDetailData {
  subscription_key_id: string;
  subscription_key: string;
  cpp_number: string;
  storage_size: number;
  usage_size: number;
  duration: number;
  bucket_name: string;
  created_by: string;
  created_at: string;
  region: string;
  endpoint_url: string;
  access_key_id: string;
  secret_key: string;
  is_active: boolean;
  expireDate: string;
  activated_at: string;
  updated_by: string;
  updated_at: string;
  leftDays: number;
  part_number: string;
  comment: string;
  expired_at: string;
  provider: CUMULUS_TYPE;
}

export interface SubscriptonKeyEuAccount extends KeyAccount {
  cpAccount?: KeyAccount;
  subAccount?: KeyAccount;
}

export interface SubscriptionKeyListResponse {
  dwKey: string;
  type: SUBSCRIPTION_TYPE;
  channel: number;
  capacity: number;
  discount: number;
  duration: number;
  createdAt: string;
  createdBy: string;
  parentAccountId: string;
  parentAccount: AccountInfo;
  generateAccountId: string;
  useAccountId: string;
  useAccount?: AccountInfo;
  updatedAt: string;
  activateDate?: string;
  registerDate?: string;
  expireDate?: string;
  replaceKey?: string;
  price: number;
  user: UserInfo;
  euAccount?: SubscriptonKeyEuAccount;
  actualPrice?: number;
  distributor?: string;
  isFreeKey: boolean;
  reference?: string;
  extendTargetKey?: string;
}

export interface KeyAccount {
  accountNumber: string;
  name: string;
  cpId: string;
  subId: string;
  euId: string;
  isActive?: boolean;
}

export interface ChangeWidthColumn extends ColumnType<SubscriptionKeyListType> {
  dataIndex: string;
  width?: number | string;
  responsive?: Breakpoint[];
}

export interface SubscriptionKeyTableAccount {
  accountNumber: string;
  name: string;
  isActive?: boolean;
}

export interface SubscriptionKeyListType extends SubscriptionKeyListResponse {
  purchased: number;
  CP?: SubscriptionKeyTableAccount;
  SubCP?: SubscriptionKeyTableAccount;
  EU?: SubscriptionKeyTableAccount;
  status: SUBSCRIPTION_STATUS;
}

export interface SubscriptonKeyListSummary {
  expireSoon: number;
  expired: number;
}

export interface SubscriptionDiscountTier {
  id: number;
  name: DISCOUNT_TIER;
  discount: number;
}

export interface SubscriptionService {
  id: number;
  name: string;
  type: SUBSCRIPTION_TYPE;
  msrp: number;
}
export interface ServiceResponse {
  service: SubscriptionService[];
}
export interface PricingResponse {
  discount: SubscriptionDiscountTier[];
}

export interface ServicePricingResponse {
  discount: SubscriptionDiscountTier[];
  service: SubscriptionService[];
  subscriptionInterval: number;
  year: YearPaymentPrice[];
}

export interface GetServicePricingResponse extends CommonResponse {
  result: ServicePricingResponse;
}

export interface GetServiceResponse extends CommonResponse {
  result: ServiceResponse;
}
export interface GetPricingResponse extends CommonResponse {
  result: PricingResponse;
}

export interface SubscriptionKeyResponse {
  list: SubscriptionKeyListResponse[];
  summary: SubscriptonKeyListSummary;
}
export interface GetSubscriptionKeyResponse extends CommonPageResponse {
  result: SubscriptionKeyResponse;
}

export interface KeyInfo {
  keyType: SUBSCRIPTION_TYPE | undefined;
  duration: number | undefined;
  number: number | undefined;
  qty: number | undefined;
  reference?: string;
  distributor?: string;
  actualPrice?: number;
  isFreeKey?: boolean;
}

export interface KeyCost {
  monthlyCost: number;
  totalCost: number;
}

export interface PostGenerateKeyRequest {
  keyType: SUBSCRIPTION_TYPE;
  duration: number;
  channels?: number;
  capacity?: number;
  qty: number;
  reference?: string;
  distributor?: string;
  actualPrice?: number;
}
export interface PostCpCheckoutKeyRequest
  extends PostGenerateKeyRequest,
    KeyCost {}

export interface PostCpCheckoutCompleteRequest extends PostGenerateKeyRequest {
  sessionId: string;
}
export interface PostCpCheckoutKeyResponse extends CommonResponse {
  result: string; // 결제 stripe url
}

export interface PostCpCheckoutCompleteResponse extends CommonResponse {
  result: string[];
}

export interface PostReplaceKeyRequest {
  key: string;
  keyType: SUBSCRIPTION_TYPE;
}

export interface MergeableFreeKeys {
  dwKey: string;
  channel: number;
  expireDate: string;
  remainingDays: number;
  remainingUnit: number;
}

export interface ActivateKey {
  key: string;
  keyType: SUBSCRIPTION_TYPE;
  duration: number;
  channels: number;
  capacity: number;
  registered: number;
  registeredChannel: number;
  allocated: number;
  preActivated: number;
  preExpire: string;
  nextExpire: string;
  userFixedStorage: number;
  mergeableFreeKeys?: MergeableFreeKeys[];
  mergeFreeKey?: string;
}

export interface PostCheckActivateResponse extends CommonResponse {
  result: ActivateKey;
}

export interface EuRequestType {
  keyType: SUBSCRIPTION_TYPE;
  requester: string;
  installer: string;
  channels: number;
  capacity: number;
  duration: number;
  message: string;
}
export interface CpRequestInfo {
  accountId: string;
  accountName: string;
  accountNumber: string;
  accountLevel: LEVEL_TYPE;
}

export type EuRequestFormType = Omit<EuRequestType, "keyType" | "duration"> & {
  keyType: SUBSCRIPTION_TYPE | undefined;
  duration: number | undefined;
};

export interface EuRequestResult {
  account: AccountInfo;
  owner: UserDetail;
  parent: AccountInfo;
}

export interface EuRequestResponse extends CommonResponse {
  result: EuRequestResult;
}

export type SubscriptionKeyDateType =
  | "registerDate"
  | "createdAt"
  | "expireDate"
  | "activateDate";

export interface SubscriptionKeyListReqeust
  extends Omit<BasePagination, "type" | "status"> {
  type?: SUBSCRIPTION_TYPE | null | "COVA";
  status?: SUBSCRIPTION_STATUS | null;
  fromDate?: string | null;
  toDate?: string | null;
  dateType?: SubscriptionKeyDateType | null;
}

export interface SubscriptionOrgInfoResponse extends CommonResponse {
  result: {
    customer: ProfileAccountInfo;
    installer: ProfileAccountInfo;
  };
}
export interface SubscriptionDetailSummaryBase {
  count: number;
  registered: number;
  activated: number;
  expiration: string;
  registeredChannel: number;
  required: Record<string, SubscriptionDevicesInfo>;
  activatedCamera: number;
  totalUnit?: number;
  dwKey: string;
  recorderName: string;
  extendedKeys?: { dwKey: string; duration: number }[];
}
export interface SubscriptionDetailSummaryResult
  extends SubscriptionDetailSummaryBase {
  covaList: SubscriptionDetailSummaryBase[];
}
export interface SubscriptionDevicesInfo {
  channel: number;
  count: number;
  displayType: RecorderType;
  model: string;
}

export interface SubscriptionDetailSummaryResponse extends CommonResponse {
  result: SubscriptionDetailSummaryResult;
}

export interface SubscriptionOrgListResult extends AccountInfo {
  registeredChannel: number;
  activateChannel: number;
  activateStorage: number;
  channelExpire: string;
  storageExpire: string;
  requested?: EuRequestType[];
  registered?: number;
  autoEnroll: boolean;
  taxExempt?: boolean;
  taxEin?: string;
  taxExemptExpiration?: string;
}
export interface SubscriptionOrgListColumnType
  extends SubscriptionOrgListResult {}
export interface SusbcriptonOrgListResponse extends CommonPageResponse {
  result: SubscriptionOrgListResult[];
}

export interface SubscriptionKeyPriceResult {
  channel: number;
  avgChannel: number;
  combineChannel: number;
  storage: number;
  avgStorage: number;
  combineStorage: number;
  totalPrice: number;
}
export interface SubscriptionKeyPriceResponse extends CommonResponse {
  result: SubscriptionKeyPriceResult;
}

export interface SendEmailRequest {
  email: string[];
  title: string;
  message: string;
}

export interface SubscriptionRequestList extends EuRequestType {
  id: string;
  createdAt: string;
}

export interface SubscriptionRequestListResponse extends CommonPageResponse {
  result: SubscriptionRequestList[];
}

export interface SubscriptionRequestTable extends SubscriptionRequestList {
  qty: number;
}
export interface SubscriptionStorageChange {
  allocated: number;
  preActivated: number;
  userFixedStorage: number;
  preExpire: string;
  nextExpire: string;
}

export interface SubscriptionStorageChangeResponse extends CommonResponse {
  result: SubscriptionStorageChange;
}

export interface ExpirationLogicKeyList {
  key: string;
  channel: number;
  remainDays: number;
  capacity: number;
}

export interface ExpirationLogicResult {
  totalCapacity: number;
  cameras: number;
  remains: number;
  expirationDate: string;
  keyList: ExpirationLogicKeyList[];
}

export interface ExpirationLogicResponse extends CommonResponse {
  result: ExpirationLogicResult;
}

export enum BILLING_CYCLE {
  TEST_2DAY = "TEST_2DAY",
  MONTHLY = "MONTHLY",
  YEARLY = "YEARLY",
  TEST_YEARLY = "TEST_YEARLY",
}

export enum PLAN_START_TERM {
  MONTHLY = "MONTHLY",
  YEAR_1 = "YEAR_1",
  YEAR_2 = "YEAR_2",
  YEAR_3 = "YEAR_3",
  TEST_MONTHLY = "TEST_MONTHLY",
  TEST_YEAR_1 = "TEST_YEAR_1",
}

export interface UsagePlanOutletContext {
  titleComponent: React.ReactNode;
  updateQueryInfo: (partial: Partial<BasePagination>) => void;
  onHeaderCell: (column: ColumnType<any>) => {
    onClick: () => void;
  };
  onChangePage: (page: number, totalRows: number) => void;
}
export interface SubscriptionBaseOutletContext {
  updateQueryInfo: (partial: Partial<BasePagination>) => void;
  onHeaderCell: (column: ColumnType<any>) => {
    onClick: () => void;
  };
  onChangePage: (page: number, totalRows: number) => void;
  setStatus: React.Dispatch<React.SetStateAction<StatusType>>;
  setButtons: (buttons: React.ReactNode) => void;
  selectedRowAccount: SelectedRowAccountInfo;
}

export interface SubscriptionPageOutletContext
  extends SubscriptionBaseOutletContext {
  onClickFilterTitle: (value: string | undefined, name: string) => void;
  tab: SUBSCRIPTION_TYPE | "REQUEST";
  isCoTermOpen: boolean;
  setIsCoTermOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setFilters: React.Dispatch<React.SetStateAction<React.ReactNode>>;
}

export interface UsageLayoutOutletContext
  extends SubscriptionBaseOutletContext {
  summary: StatCardResultDto;
  onOpenAddCloudModal: (account: CloudModalAccount) => void;
  selectedGraphDate: string;
}

export enum EVENT_HISTORY_TYPE {
  CHANNEL_ADD = "CHANNEL_ADD",
  CHANNEL_DEL = "CHANNEL_DEL",
  STORAGE_ADD = "STORAGE_ADD",
  STORAGE_DEL = "STORAGE_DEL",
  ORG_ADD = "ORG_ADD",
  ORG_DEL = "ORG_DEL",
  PLAN_START = "PLAN_START",
  PLAN_CANCEL = "PLAN_CANCEL",
  PAYMENT = "PAYMENT",
  USAGE_MONTH = "USAGE_MONTH",
  ORG_SCHE_ADD = "ORG_SCHE_ADD",
}

export enum EVENT_HISTORY_CHANGE_UNIT {
  CHANNEL = "CH",
  STORAGE = "TB",
  TEXT = "TEXT",
}

export interface EventToHistoryPresenterProps {
  columns: ColumnType<EventHistoryDto>[];
  data: EventHistoryDto[];
  onChangePage: (page: number, totalRows: number) => void;
  queryInfo: BasePagination;
  isFetching: boolean;
}
